import React, { useState } from "react";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { CONTACT, CONTACT_INTRO, IMAGES } from "../mock";
import { useToast } from "../hooks/use-toast";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const HELP_OPTIONS = [
  "TMS treatment",
  "Accelerated TMS",
  "Esketamine",
  "Depression",
  "Anxiety",
  "OCD",
];

export default function ContactSection() {
  const { toast } = useToast();
  const [form, setForm] = useState({
    location: "Sydney",
    name: "",
    email: "",
    phone: "",
    help: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const handleChange = (e) => set(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.location || !form.name || !form.email || !form.phone || !form.help) {
      toast({
        title: "Please complete all required fields",
        description: "Location, name, email, phone and how we can help are required.",
      });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source: "contact" }),
      });
      if (!res.ok) throw new Error("Request failed");
      setForm({ location: "Sydney", name: "", email: "", phone: "", help: "", message: "" });
      toast({
        title: "Enquiry sent",
        description: "Thank you for reaching out to AwareHub. We'll be in touch soon.",
      });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: "Your enquiry could not be sent. Please try again or call 1300 029 273.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-brand-200 bg-sand-50 px-4 py-3 text-brand-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition";

  return (
    <section id="contact" className="py-12 sm:py-16 bg-sand-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center mb-10 reveal">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-900 max-w-3xl mx-auto text-balance">
            {CONTACT_INTRO.heading}
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">
          {/* Form */}
          <div className="bg-white rounded-3xl p-7 sm:p-9 shadow-[0_20px_60px_-30px_rgba(31,56,53,0.35)] border border-brand-100 reveal">
            <h3 className="font-display text-2xl text-brand-900 mb-6">Enquire Now</h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="flex items-center gap-6 text-brand-900/80 text-sm">
                {["Sydney", "Bundaberg"].map((loc) => (
                  <label key={loc} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="location"
                      value={loc}
                      checked={form.location === loc}
                      onChange={handleChange}
                      className="accent-brand-600 h-4 w-4"
                      data-testid={`contact-location-${loc.toLowerCase()}`}
                    />
                    {loc}
                  </label>
                ))}
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-900/80 mb-1.5">Name *</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="Your name"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-900/80 mb-1.5">Email *</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="you@example.com"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-900/80 mb-1.5">Phone *</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="Your phone number"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-900/80 mb-1.5">How can we help you? *</label>
                <Select value={form.help} onValueChange={(v) => set("help", v)}>
                  <SelectTrigger className={`${inputCls} h-auto text-base`} data-testid="contact-help-select">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent>
                    {HELP_OPTIONS.map((o) => (
                      <SelectItem key={o} value={o}>
                        {o}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-900/80 mb-1.5">Additional information</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={4}
                  className={`${inputCls} resize-none`}
                  placeholder="Any additional information to assist you best?"
                  data-testid="contact-message-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-cta-500 hover:bg-cta-600 disabled:opacity-60 text-white px-7 py-3 font-semibold transition-colors"
                data-testid="contact-submit-button"
              >
                <Send className="h-4 w-4" />
                {submitting ? "Sending..." : "Send"}
              </button>
              <p className="text-xs text-brand-900/50 pt-1">
                This site is protected by reCAPTCHA and the Google Privacy Policy and Terms of Service apply.
              </p>
            </form>
          </div>

          {/* Details */}
          <div className="space-y-6 reveal">
            <div className="bg-brand-900 rounded-3xl overflow-hidden text-white">
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={IMAGES.contactReception}
                  alt="AwareHub clinic reception"
                  className="h-full w-full object-cover"
                  data-testid="contact-section-photo"
                />
              </div>
              <div className="p-7 sm:p-9">
                <h3 className="font-display text-2xl mb-6">AwareHub</h3>
                <a href={CONTACT.phoneHref} className="flex items-center gap-3 text-brand-100 hover:text-white transition-colors mb-6">
                  <span className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <Phone className="h-4.5 w-4.5" />
                  </span>
                  <span className="text-lg font-semibold">{CONTACT.phone}</span>
                </a>
                <div className="space-y-6">
                  {CONTACT.locations.map((loc) => (
                    <div key={loc.name} className="border-t border-white/10 pt-5">
                      <p className="text-xs font-semibold tracking-widest text-brand-300 mb-2">{loc.name}</p>
                      <p className="flex items-start gap-3 text-sm text-brand-100">
                        <MapPin className="h-4.5 w-4.5 mt-0.5 shrink-0 text-brand-300" />
                        <span>{loc.address}</span>
                      </p>
                      <a href={`mailto:${loc.email}`} className="mt-2 flex items-center gap-3 text-sm text-brand-100 hover:text-white transition-colors">
                        <Mail className="h-4.5 w-4.5 shrink-0 text-brand-300" />
                        {loc.email}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
